import React from 'react';
import { Typography, Grid, TextField, Button, MenuItem, Select, FormControl, InputLabel, IconButton, Paper, Box, Tooltip, Divider, OutlinedInput, FormHelperText } from '@mui/material';
import { EnhancedToggle } from '../common/EnhancedToggle';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { v4 as uuidv4 } from 'uuid';
import { useScenarios } from '../../context/ScenarioContext';



const incomeTypes = [
  { value: 'spousal', label: 'Spousal Income' },
  { value: 'secondJob', label: 'Second Job Income' },
  { value: 'rental', label: 'Rental Income' },
  { value: 'annuity', label: 'Annuity' },
  { value: 'other', label: 'Other (Specify)' },
];

const frequencyTypes = [
  { value: 'oneTime', label: 'One-Time' },
  { value: 'ongoing', label: 'Ongoing' },
];

const increasingTypes = [
  { value: 'fixed', label: 'Fixed (No Increase)' },
  { value: 'percentage', label: 'Annual % Increase' },
  { value: 'amount', label: 'Annual Flat Amount Increase' },
];

const AdditionalIncomeForm = ({ scenario, handleScenarioChange }) => {
  const { scenarios, toggleIncomeForScenario, isIncomeIncludedInScenario } = useScenarios();
  const currentYear = new Date().getFullYear();

  const handleAddIncome = () => {
    const newIncome = {
      id: uuidv4(),
      description: '',
      type: 'spousal',
      frequency: 'ongoing',
      amount: 0,
      startYear: currentYear,
      // Use Number.MAX_SAFE_INTEGER to represent "No end" by default
      endYear: Number.MAX_SAFE_INTEGER,
      increasingType: 'fixed',
      increaseRate: 0,
    };
    const updatedIncomes = [...(scenario.additionalIncomes || []), newIncome];
    handleScenarioChange(scenario.id, 'additionalIncomes', updatedIncomes);
    
    // Also add this income source to the current scenario's includedIncomes array
    toggleIncomeForScenario(newIncome.id, scenario.id, true);
  };

  const handleUpdateIncome = (index, field, value) => {
    const updatedIncomes = [...scenario.additionalIncomes];
    let parsedValue = value;
    if (field === 'amount' || field === 'increaseRate') {
      parsedValue = parseFloat(value) || 0;
    } else if (field === 'startYear') {
      // Allow empty string temporarily while typing
      parsedValue = value === '' ? '' : (parseInt(value) || currentYear);
    } else if (field === 'endYear') {
      // Handle dropdown and direct input for endYear
      if (value === 'no-end') {
        parsedValue = Number.MAX_SAFE_INTEGER; // Use MAX_SAFE_INTEGER to represent "No end"
      } else if (typeof value === 'string' && value !== '') {
        // Try to parse string input
        parsedValue = parseInt(value) || currentYear;
      } else if (typeof value === 'number') {
        parsedValue = value;
      } else {
        parsedValue = currentYear;
      }
    }
    updatedIncomes[index] = { ...updatedIncomes[index], [field]: parsedValue };

    // Basic validation for years
    if (field === 'startYear') {
      // If end year is set (not "No end") and is less than new start year, update it
      if (updatedIncomes[index].endYear !== Number.MAX_SAFE_INTEGER && 
          updatedIncomes[index].endYear < parsedValue) {
        updatedIncomes[index].endYear = parsedValue;
      }
    }
    
    // For endYear, allow any valid input, ensuring it's not before startYear
    if (field === 'endYear' && parsedValue !== Number.MAX_SAFE_INTEGER) {
      // Only apply start year validation if not "No end"
      if (parsedValue < updatedIncomes[index].startYear) {
        updatedIncomes[index].endYear = updatedIncomes[index].startYear;
      }
    }
    handleScenarioChange(scenario.id, 'additionalIncomes', updatedIncomes);
  };

  const handleRemoveIncome = (index) => {
    const updatedIncomes = scenario.additionalIncomes.filter((_, i) => i !== index);
    handleScenarioChange(scenario.id, 'additionalIncomes', updatedIncomes);
  };

  return (
    <Box mt={2}>
      {(scenario.additionalIncomes || []).map((income, index) => (
        <Paper key={income.id} sx={{ padding: 2, marginBottom: 3, position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 1 }} elevation={1}>
          <IconButton 
            aria-label="delete income source"
            sx={{ position: 'absolute', top: 0.5, right: 0.5 }} 
            size="small" 
            onClick={() => handleRemoveIncome(index)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                value={income.description}
                onChange={(e) => handleUpdateIncome(index, 'description', e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
                sx={{ height: '56px' }}
                placeholder={income.type === 'other' ? "E.g., 'Consulting Gig'" : "E.g., 'Partner\'s Salary'"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth margin="dense" sx={{ 
                height: '56px', 
                minWidth: '180px'
              }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={income.type}
                  onChange={(e) => handleUpdateIncome(index, 'type', e.target.value)}
                  label="Type"
                  MenuProps={{ 
                    PaperProps: { 
                      style: { maxHeight: 300 } 
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                  }}
                >
                  {incomeTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl variant="outlined" fullWidth margin="dense" sx={{ 
                height: '56px', 
                minWidth: '180px',
                marginTop: 1, 
                marginBottom: 1
              }}>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={income.frequency}
                  onChange={(e) => handleUpdateIncome(index, 'frequency', e.target.value)}
                  label="Frequency"
                  MenuProps={{ 
                    PaperProps: { 
                      style: { maxHeight: 300 } 
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                  }}
                >
                  {frequencyTypes.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={income.frequency === 'oneTime' ? "Amount" : "Annual Amount"}
                type="number"
                value={income.amount}
                onChange={(e) => handleUpdateIncome(index, 'amount', e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
                sx={{ marginTop: 1, marginBottom: 1, height: '56px' }}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={income.frequency === 'oneTime' ? "Year of Income" : "Start Year"}
                type="number"
                value={income.startYear}
                onChange={(e) => handleUpdateIncome(index, 'startYear', e.target.value)}
                fullWidth
                variant="outlined"
                margin="dense"
                sx={{ marginTop: 1, marginBottom: 1, height: '56px' }}
                InputProps={{ inputProps: { min: currentYear - 70, max: currentYear + 70 } }}
              />
            </Grid>

            {income.frequency === 'ongoing' && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl variant="outlined" fullWidth margin="dense" sx={{
                    height: '56px',
                    marginTop: 1,
                    marginBottom: 1,
                    minWidth: '150px' // Adjusted width
                  }}>
                    <InputLabel id={`end-year-label-${index}`}>End Year</InputLabel>
                    <Select
                      labelId={`end-year-label-${index}`}
                      value={income.endYear === Number.MAX_SAFE_INTEGER ? 'no-end' : income.endYear}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'no-end') {
                          handleUpdateIncome(index, 'endYear', Number.MAX_SAFE_INTEGER);
                        } else {
                          handleUpdateIncome(index, 'endYear', value);
                        }
                      }}
                      onOpen={() => {
                        // Prepare for direct input by selecting the value
                        const input = document.querySelector(`#end-year-select-${index}`);
                        if (input) {
                          setTimeout(() => {
                            input.focus();
                          }, 100);
                        }
                      }}
                      label="End Year"
                      inputProps={{
                        id: `end-year-select-${index}`,
                      }}
                      input={
                        <OutlinedInput
                          label="End Year"
                          error={income.endYear < income.startYear}
                        />
                      }
                      MenuProps={{
                        PaperProps: { 
                          style: { maxHeight: 300 } 
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                      }}
                      // Enable typing directly in the select input
                      freeSolo
                      renderValue={(selected) => {
                        if (selected === 'no-end') return "No end";
                        return selected;
                      }}
                    >
                      {/* No end option */}
                      <MenuItem value="no-end">No end</MenuItem>
                      
                      {/* Generate years from start year to start+50 */}
                      {Array.from({ length: 51 }, (_, i) => income.startYear + i).map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                    {income.endYear < income.startYear && income.endYear !== Number.MAX_SAFE_INTEGER && (
                      <FormHelperText error>End year cannot be before start year</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl variant="outlined" fullWidth margin="dense" sx={{ 
                    height: '56px', 
                    minWidth: '180px',
                    marginTop: 1, 
                    marginBottom: 1
                  }}>
                    <InputLabel>Increase Type</InputLabel>
                    <Select
                      value={income.increasingType}
                      onChange={(e) => handleUpdateIncome(index, 'increasingType', e.target.value)}
                      label="Increase Type"
                      MenuProps={{ 
                        PaperProps: { 
                          style: { maxHeight: 300 } 
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {increasingTypes.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {income.increasingType !== 'fixed' && (
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label={income.increasingType === 'percentage' ? "Increase Rate (%)" : "Increase Amount ($)"}
                      type="number"
                      value={income.increaseRate}
                      onChange={(e) => handleUpdateIncome(index, 'increaseRate', e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      sx={{ marginTop: 1, marginBottom: 1, height: '56px' }}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
          
          {/* Scenario Toggles Section */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Include in Scenarios:
          </Typography>
          
          <Grid container spacing={1}>
            {scenarios.map(s => (
              <Grid item xs={6} sm={4} md={3} key={s.id}>
                <EnhancedToggle
                  size="small"
                  checked={isIncomeIncludedInScenario(income.id, s.id)}
                  onChange={(e) => toggleIncomeForScenario(income.id, s.id, e.target.checked)}
                  label={s.name}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ))}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddIncome}
        sx={{ mt: 2 }}
      >
        Add Income Source
      </Button>
    </Box>
  );
};

export default AdditionalIncomeForm;
